import classNames from "classnames";
import * as PropTypes from "prop-types";
import Waypoint from "react-waypoint";
import { Motion, spring } from "react-motion";
import KeyHandler, { KEYPRESS } from "react-key-handler";
import { colorInterpolation } from "color-interpolator";
import { Subscribe } from "unstated";

import variablesStyles from "/styles/variables.css";
import inDepthOverlayStyles from "./InDepthOverlay.css";
import { KEYCODES } from "/javascripts/constants/keyCodes";
import { MOTION_PRESETS } from "/react/constants/motionPresets";
import { getAbsoluteTopOfElement } from "/javascripts/utils/getAbsoluteTopOfElement";

import { withPageJump } from "/react/utils/withPageJump";
import { withViewport } from "/react/utils/withViewport";

import {
  AnimatedPageScroll,
  ContentBoxLink,
  HeadingSubHeader,
  IconPlus,
  MiniNavigationContainer,
  ScrollLock,
  TextCopy,
} from "/react/library";

@withPageJump()
@withViewport()
export class InDepthOverlay extends React.PureComponent {
  static propTypes = {
    background: PropTypes.element.isRequired,
    content: PropTypes.element.isRequired,
    contentPositionMobile: PropTypes.oneOf(["bottom"]),
    hasMultiLineTitlesOnMobile: PropTypes.bool,
    heading: PropTypes.string.isRequired,
    onOpenAnimationEnd: PropTypes.func,
    text: PropTypes.object.isRequired,
  };

  containerRef = React.createRef();
  sectionAbsoluteTop = 0;
  springConfig = MOTION_PRESETS.FAST;

  state = {
    hasBeenOpened: false,
    isPageScrolling: false,
    isOpen: false,
    scrollToOffset: 0,
    textAnimationStep: 0,
  };

  shouldAnimateToEnd = () => {
    return (
      this.state.textAnimationStep < 2 &&
      this.props.pageIsJumping &&
      this.props.pageHasJumpedToPosition > this.sectionAbsoluteTop
    );
  };

  goToNextTextAnimationStep = () => {
    this.setState((prevState) => {
      return {
        textAnimationStep: prevState.textAnimationStep + 1,
      };
    });
  };

  onClickButtonAnimation = (event, miniNavigation) => {
    const { isOpen } = this.state;

    if (isOpen) {
      miniNavigation.show();
    } else {
      miniNavigation.hide();
    }

    this.bus && this.bus.emit("onToggleHeader", !isOpen);
    this.toggleOverlay(event);
  };

  toggleOverlay = (event) => {
    event.preventDefault();
    this.setState((prevState) => {
      const nextState = {
        isOpen: !prevState.isOpen,
      };

      if (!prevState.isOpen) {
        nextState.hasBeenOpened = true;
        nextState.isPageScrolling = true;
        nextState.scrollToOffset =
          0 - this.containerRef.current.getBoundingClientRect().top;
      }

      return nextState;
    });
  };

  handleOpenAnimationEndEvent = () => {
    if (!this.state.isOpen) return;

    const openAnimationCallback = this.props.onOpenAnimationEnd;

    openAnimationCallback && openAnimationCallback();
  };

  handleMountAndUpdate = () => {
    if (this.shouldAnimateToEnd()) {
      this.setState({
        textAnimationStep: 2,
      });
    }
  };

  componentDidUpdate() {
    this.handleMountAndUpdate();
  }

  async componentDidMount() {
    // Dynamically import because "window" is not defined when using SSR
    const module = await import("/javascripts/bus");
    this.bus = module.default;

    this.sectionAbsoluteTop = getAbsoluteTopOfElement(
      this.containerRef.current
    );
    this.handleMountAndUpdate();

    this.bus && this.bus.on("windowResize", this.handleResize);
  }

  componentWillUnmount() {
    this.bus && this.bus.off("windowResize", this.handleResize);
  }

  handleResize = (event) => {
    this.setState((prevState) => {
      if (prevState.isOpen) {
        prevState.scrollToOffset =
          0 - this.containerRef.current.getBoundingClientRect().top;
      }
      return prevState;
    });
  };

  render() {
    const {
      background,
      content,
      contentPositionMobile,
      hasMultiLineTitlesOnMobile,
      heading,
      text,
      viewportIsTabletOrBigger,
    } = this.props;

    const { hasBeenOpened, isOpen, scrollToOffset, textAnimationStep } =
      this.state;

    const scrollBackToOriginalPosition = !isOpen && hasBeenOpened;

    const animationProps = {
      isOpen,
      springConfig: this.springConfig,
    };

    const buttonAnimationProps = {
      ...animationProps,
      goToNextTextAnimationStep: this.goToNextTextAnimationStep,
      hasBeenOpened,
      textAnimationStep,
      viewportIsTabletOrBigger,
    };

    const copyAnimationProps = {
      ...animationProps,
      goToNextTextAnimationStep: this.goToNextTextAnimationStep,
      hasMultiLineTitlesOnMobile,
      heading,
      text,
      textAnimationStep,
      viewportIsTabletOrBigger,
    };

    const contentAnimationProps = {
      ...animationProps,
      content,
      contentPositionMobile,
    };

    const backgroundAnimationProps = {
      ...animationProps,
      background,
      onAnimationEnd: this.handleOpenAnimationEndEvent,
    };

    return (
      <React.Fragment>
        <Subscribe to={[MiniNavigationContainer]}>
          {(miniNavigation) => (
            <React.Fragment>
              <div
                className={inDepthOverlayStyles.container}
                ref={this.containerRef}
              >
                <div className={inDepthOverlayStyles.uiContainer}>
                  <div className={inDepthOverlayStyles.uiWrapper}>
                    <ButtonAnimation
                      {...buttonAnimationProps}
                      onClick={(evt) =>
                        this.onClickButtonAnimation(evt, miniNavigation)
                      }
                    />
                    <CopyAnimation {...copyAnimationProps} />
                  </div>
                </div>
                <ContentAnimation {...contentAnimationProps} />
                <BackgroundAnimation {...backgroundAnimationProps} />
              </div>
              {isOpen && (
                <React.Fragment>
                  <ScrollLock />
                  <AnimatedPageScroll
                    scrollToTarget={this.containerRef.current}
                    onRest={() => this.setState({ isPageScrolling: false })}
                  />
                  <KeyHandler
                    keyEventname={KEYPRESS}
                    code={KEYCODES.ESC}
                    onKeyHandle={(evt) =>
                      this.onClickButtonAnimation(evt, miniNavigation)
                    }
                  />
                </React.Fragment>
              )}
              {scrollBackToOriginalPosition && (
                <React.Fragment>
                  <ScrollLock />
                  <AnimatedPageScroll
                    offset={scrollToOffset}
                    scrollToTarget={this.containerRef.current}
                    onRest={() =>
                      this.setState({
                        hasBeenOpened: false,
                        isPageScrolling: false,
                      })
                    }
                  />
                </React.Fragment>
              )}
              <Waypoint onEnter={this.goToNextTextAnimationStep} />
            </React.Fragment>
          )}
        </Subscribe>
      </React.Fragment>
    );
  }
}

const getFillColor = (step) =>
  colorInterpolation(
    variablesStyles.colorBlueDark,
    variablesStyles.colorWhite,
    step
  );

const ButtonAnimation = (props) => (
  <Motion
    defaultStyle={{
      backgroundOpacity: 1,
      fillColor: 0,
      iconRotate: 0,
      opacity: 0,
      scale: 0,
      top: 0,
    }}
    style={{
      backgroundOpacity: props.isOpen
        ? spring(0, props.springConfig)
        : spring(1, props.springConfig),
      fillColor: props.isOpen
        ? spring(1, props.springConfig)
        : spring(0, props.springConfig),
      iconRotate: props.isOpen
        ? spring(45, props.springConfig)
        : spring(0, props.springConfig),
      opacity: props.textAnimationStep >= 1 ? spring(1, props.springConfig) : 0,
      top: props.isOpen
        ? spring(
            props.viewportIsTabletOrBigger ? -50 : -100,
            props.springConfig
          )
        : spring(0, props.springConfig),
      scale: props.textAnimationStep >= 1 ? spring(1, props.springConfig) : 0,
    }}
    onRest={props.goToNextTextAnimationStep}
  >
    {(interpolatedStyles) => {
      const fillColor = getFillColor(interpolatedStyles.fillColor);

      return (
        <button
          className={classNames(inDepthOverlayStyles.toggleButton, {
            [inDepthOverlayStyles.toggleButtonHasBlipAnimation]:
              !props.hasBeenOpened && props.textAnimationStep >= 2,
          })}
          style={{
            backgroundColor: `rgba(255, 255, 255, ${interpolatedStyles.backgroundOpacity})`,
            borderColor: fillColor,
            opacity: interpolatedStyles.opacity,
            transform: `translate3d(0, ${interpolatedStyles.top}px, 0) scale(${interpolatedStyles.scale})`,
          }}
          onClick={props.onClick}
        >
          <IconPlus
            fill={fillColor}
            style={{
              transform: `rotate(${interpolatedStyles.iconRotate}deg)`,
              zIndex: 2,
            }}
          />
        </button>
      );
    }}
  </Motion>
);

const CopyAnimation = (props) => {
  const textDeltaLineAnimation = 150;
  const mobileTop =
    props.hasMultiLineTitlesOnMobile && !props.viewportIsTabletOrBigger
      ? -80
      : -85;
  const textDeltaOpenAnimation = props.hasMultiLineTitlesOnMobile ? 20 : 30;

  return (
    <Motion
      defaultStyle={{
        fillColor: 0,
        textTopLineAnimation: 0,
        textTopOpenAnimation: 0,
        top: 0,
      }}
      style={{
        fillColor: props.isOpen
          ? spring(1, props.springConfig)
          : spring(0, props.springConfig),
        textTopLineAnimation:
          props.textAnimationStep >= 2
            ? spring(textDeltaLineAnimation, props.springConfig)
            : spring(0, props.springConfig),
        textTopOpenAnimation: props.isOpen
          ? spring(textDeltaOpenAnimation, props.springConfig)
          : spring(0, props.springConfig),
        top: props.isOpen
          ? spring(
              props.viewportIsTabletOrBigger ? -50 : mobileTop,
              props.springConfig
            )
          : spring(0, props.springConfig),
      }}
    >
      {(interpolatedStyles) => (
        <div
          className={inDepthOverlayStyles.copyContainer}
          style={{
            transform: `translate3d(0, ${interpolatedStyles.top}px, 0)`,
          }}
        >
          <div
            className={classNames(inDepthOverlayStyles.copyCtaButtonContainer, {
              [inDepthOverlayStyles.isHidden]: !props.isOpen,
            })}
            style={{
              transform: `translate3d(0, ${interpolatedStyles.textTopOpenAnimation}%, 0)`,
            }}
          >
            <ContentBoxLink
              // href='/content/uploads/2018/11/Epic_E1000_Spec_Sheet_v3.pdf'
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              isLight
            >
              Download full specs
            </ContentBoxLink>
          </div>
          <div className={inDepthOverlayStyles.copyAnimationContainer}>
            <div
              className={inDepthOverlayStyles.heading}
              style={{
                transform: `translate3d(0, ${interpolatedStyles.textTopOpenAnimation}%, 0)`,
              }}
            >
              <HeadingSubHeader
                style={{
                  color: getFillColor(interpolatedStyles.fillColor),
                  transform: `translate3d(0, ${
                    textDeltaLineAnimation -
                    interpolatedStyles.textTopLineAnimation
                  }%, 0)`,
                }}
              >
                {props.heading}
              </HeadingSubHeader>
            </div>
            <div
              className={inDepthOverlayStyles.text}
              style={{
                transform: `translate3d(0, ${interpolatedStyles.textTopOpenAnimation}%, 0)`,
              }}
            >
              <TextCopy
                style={{
                  color: getFillColor(interpolatedStyles.fillColor),
                  transform: `translate3d(0, ${
                    0 -
                    textDeltaLineAnimation +
                    interpolatedStyles.textTopLineAnimation
                  }%, 0)`,
                }}
              >
                {props.text}
              </TextCopy>
            </div>
          </div>
        </div>
      )}
    </Motion>
  );
};

const ContentAnimation = (props) => (
  <Motion
    defaultStyle={{
      top: 100,
    }}
    style={{
      top: props.isOpen
        ? spring(0, props.springConfig)
        : spring(100, props.springConfig),
    }}
  >
    {(interpolatedStyles) => (
      <div
        className={inDepthOverlayStyles.contentContainer}
        style={{ top: `${interpolatedStyles.top}%` }}
      >
        <div
          className={classNames(
            "u-layout-contentWrapper",
            inDepthOverlayStyles.contentWrapper,
            {
              [inDepthOverlayStyles.contentWrapperHasBottomPosition]:
                props.contentPositionMobile === "bottom",
            }
          )}
        >
          {props.content}
        </div>
      </div>
    )}
  </Motion>
);

const BackgroundAnimation = (props) => (
  <Motion
    defaultStyle={{
      opacity: 0,
      top: 100,
    }}
    style={{
      opacity: props.isOpen ? 1 : 0,
      top: props.isOpen
        ? spring(0, props.springConfig)
        : spring(100, props.springConfig),
    }}
    onRest={props.onAnimationEnd}
  >
    {(interpolatedStyles) => (
      <div
        className={inDepthOverlayStyles.backgroundContainer}
        style={{
          opacity: interpolatedStyles.opacity,
          top: `${interpolatedStyles.top}%`,
        }}
      >
        {props.background}
      </div>
    )}
  </Motion>
);
